/**
 * Privacy Policy Page
 * Details CalWMP's data collection, usage, and privacy practices
 */

import { Link } from 'react-router-dom';

export function Privacy() {
  const lastUpdated = '2025-09-30';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Privacy Policy</h1>

      <p className="text-sm text-neutral-600 mb-8">
        <strong>Last Updated:</strong> {lastUpdated}
      </p>

      <div className="prose prose-neutral max-w-none">
        {/* Introduction */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Introduction</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy and is committed to protecting your personal
            information. This Privacy Policy explains how we collect, use, store, and protect information when you use our
            waste management plan creation service at calwmp.com (the &quot;Service&quot;).
          </p>
          <p className="text-neutral-700 leading-relaxed">
            By using CalWMP, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Information We Collect</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">1. Information You Provide</h3>

          <h4 className="text-lg font-semibold text-neutral-900 mb-2">Guest Mode Users:</h4>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Email Address:</strong> When you request a shareable link to access your plan</li>
            <li><strong>Plan Data:</strong> Project details, waste streams, and compliance information you enter</li>
          </ul>

          <h4 className="text-lg font-semibold text-neutral-900 mb-2">Authenticated Account Users:</h4>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Email Address:</strong> Used for account creation and authentication</li>
            <li><strong>Account Data:</strong> Profile information, saved plans, and usage history</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">2. Information Collected Automatically</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Browser Information:</strong> Browser type, version, and operating system</li>
            <li><strong>Usage Data:</strong> Pages visited, time spent, features used</li>
            <li><strong>Device Information:</strong> IP address, device type, screen resolution</li>
            <li><strong>Cookies and Similar Technologies:</strong> Used to maintain session state and preferences</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">3. Browser Local Storage (Guest Mode)</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-4">
            <p className="text-blue-900">
              <strong>Important:</strong> When using guest mode, your plan data is stored in your browser&apos;s local storage
              and never leaves your device until you request a shareable link. We cannot access or recover guest mode data
              stored locally in your browser. If you clear your browser data, this information will be permanently lost.
            </p>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">How We Use Your Information</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">We use collected information for the following purposes:</p>

          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Service Delivery:</strong> Create, save, and manage your waste management plans</li>
            <li><strong>Communication:</strong> Send you shareable links and respond to support requests</li>
            <li><strong>Service Improvement:</strong> Analyze usage patterns to enhance features and user experience</li>
            <li><strong>Security:</strong> Detect and prevent fraud, abuse, or security threats</li>
            <li><strong>Compliance:</strong> Meet legal obligations and enforce our Terms of Service</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed">
            <strong>We do not:</strong> Sell your personal information, use it for advertising, or share it with third parties
            for marketing purposes.
          </p>
        </section>

        {/* Data Storage and Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Data Storage and Security</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Storage Infrastructure</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP uses Cloudflare Workers and Cloudflare D1 database for data storage and processing. Your data may be
            stored and processed in the United States and other locations where Cloudflare operates facilities.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Security Measures</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of data at rest in our databases</li>
            <li>Secure authentication mechanisms</li>
            <li>Regular security assessments</li>
            <li>Access controls and monitoring</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed mb-4">
            While we implement industry-standard security measures, no method of transmission or storage is 100% secure.
            We cannot guarantee absolute security of your information.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Data Retention</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Guest Mode (Shareable Links):</strong> Plans remain accessible for 30 days from creation</li>
            <li><strong>Authenticated Accounts:</strong> Data retained indefinitely until you delete your account</li>
            <li><strong>Analytics Data:</strong> Aggregated and anonymized data may be retained for analysis</li>
          </ul>
        </section>

        {/* How We Share Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">How We Share Information</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We do not sell, trade, or rent your personal information. We may share information only in the following limited
            circumstances:
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Service Providers</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We work with trusted third-party service providers who assist in operating our Service:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Cloudflare:</strong> Hosting, database, CDN, and security services (<a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</a>)</li>
            <li><strong>Email Service:</strong> Sending shareable links and support responses</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            These providers are contractually obligated to protect your data and use it only for authorized purposes.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Legal Requirements</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We may disclose your information if required by law, legal process, or government request, or to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Comply with legal obligations</li>
            <li>Protect our rights, property, or safety</li>
            <li>Prevent fraud or security threats</li>
            <li>Protect users or the public</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Business Transfers</h3>
          <p className="text-neutral-700 leading-relaxed">
            If CalWMP is involved in a merger, acquisition, or asset sale, your information may be transferred. We will
            provide notice before your information becomes subject to a different privacy policy.
          </p>
        </section>

        {/* Your Rights and Choices */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Your Rights and Choices</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">California Residents (CCPA Rights)</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you are a California resident, you have the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Know:</strong> What personal information we collect, use, and disclose</li>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Delete:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-Out:</strong> Opt out of sale or sharing (we don&apos;t sell or share your data)</li>
            <li><strong>Non-Discrimination:</strong> Exercise these rights without discrimination</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">All Users</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Access Your Data:</strong> View your plans and account information within the Service</li>
            <li><strong>Update Your Data:</strong> Edit your plans and account details at any time</li>
            <li><strong>Delete Your Account:</strong> Request account deletion by contacting support</li>
            <li><strong>Export Your Data:</strong> Download your plans as PDF documents</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed mb-4">
            To exercise these rights, contact us at <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>.
            We will respond within 30 days of verification of your identity.
          </p>
        </section>

        {/* Cookies and Tracking */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Cookies and Tracking Technologies</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">What We Use</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We use cookies and similar technologies for:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Essential Cookies:</strong> Required for authentication and basic functionality</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and choices</li>
            <li><strong>Analytics:</strong> Understand how users interact with our Service</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Do Not Track (DNT)</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We currently do not respond to Do Not Track browser signals. However, you can manage cookies through your
            browser settings. Disabling essential cookies may affect Service functionality.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Third-Party Tracking</h3>
          <p className="text-neutral-700 leading-relaxed">
            We do not allow third parties to collect personally identifiable information about your activities across
            different websites when you use our Service.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Children&apos;s Privacy</h2>
          <p className="text-neutral-700 leading-relaxed">
            CalWMP is not intended for children under 13 years of age. We do not knowingly collect personal information
            from children under 13. If you are a parent or guardian and believe your child has provided us with personal
            information, please contact us, and we will delete such information.
          </p>
        </section>

        {/* International Users */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">International Users</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is operated in the United States. If you access our Service from outside the United States, your
            information will be transferred to, stored, and processed in the United States and other countries where our
            service providers operate.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            By using our Service, you consent to the transfer of your information to countries that may have different
            data protection laws than your country of residence.
          </p>
        </section>

        {/* Changes to Privacy Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Changes to This Privacy Policy</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal,
            operational, or regulatory reasons. When we make changes, we will:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Update the &quot;Last Updated&quot; date at the top of this policy</li>
            <li>Notify you of significant changes through the Service or via email</li>
            <li>Post the updated policy on this page</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            Your continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Us</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
            <p className="text-neutral-900 mb-2">
              <strong>Email:</strong> <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>
            </p>
            <p className="text-neutral-900">
              <strong>Response Time:</strong> We aim to respond to all privacy inquiries within 5 business days.
            </p>
          </div>
        </section>

        {/* Related Policies */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Related Policies</h2>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <Link to="/terms" className="text-primary-600 hover:text-primary-700 underline">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/disclaimer" className="text-primary-600 hover:text-primary-700 underline">
                Disclaimer
              </Link>
            </li>
            <li>
              <Link to="/accessibility" className="text-primary-600 hover:text-primary-700 underline">
                Accessibility Statement
              </Link>
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          This Privacy Policy was last updated on {lastUpdated}. Previous versions are available upon request.
        </p>
      </div>
    </div>
  );
}